/*
 * Events the reader said they're going to ("RSVP"), kept on this device only
 * (localStorage `baws_event_rsvps`), each with a reminder 15 minutes before
 * the start (reminders.js). Like pins, an RSVP stores a snapshot of the card
 * so it survives the event rotating out of the feed.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  scheduleReminder,
  cancelReminder,
  armWebReminders,
  listenForReminderTaps,
} from './reminders';

const KEY = 'baws_event_rsvps';
const EVENT = 'baws-event-rsvps';
const MAX = 100;

export function readRsvps() {
  try {
    const list = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(list) ? list.filter((r) => r?.item?.event?.start) : [];
  } catch (e) {
    return [];
  }
}

export function writeRsvps(list) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
  } catch (e) {
    // storage full / blocked: RSVP just won't persist
  }
  window.dispatchEvent(new Event(EVENT));
}

const snapshot = (item) => {
  const { score, onThisDay, ...rest } = item;
  return rest;
};

/** RSVP to an event: store it and schedule the reminder. */
export async function rsvp(item) {
  const list = readRsvps().filter((r) => r.item.id !== item.id);
  const entry = { item: snapshot(item), at: Date.now(), reminder: null };
  writeRsvps([entry, ...list]);
  const reminder = await scheduleReminder(entry.item);
  writeRsvps(
    readRsvps().map((r) => (r.item.id === item.id ? { ...r, reminder } : r))
  );
  return reminder;
}

export async function cancelRsvp(id) {
  const entry = readRsvps().find((r) => r.item.id === id);
  writeRsvps(readRsvps().filter((r) => r.item.id !== id));
  if (entry) await cancelReminder(entry.item);
}

/** { rsvps, isGoing(id), reminderFor(id), toggle(item) } */
export function useRsvps() {
  const [rsvps, setRsvps] = useState(readRsvps);

  useEffect(() => {
    const sync = () => setRsvps(readRsvps());
    window.addEventListener(EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const find = useCallback(
    (id) => rsvps.find((r) => r.item.id === id),
    [rsvps]
  );

  return {
    rsvps,
    isGoing: (id) => !!find(id),
    reminderFor: (id) => find(id)?.reminder || null,
    toggle: (item) => (find(item.id) ? cancelRsvp(item.id) : rsvp(item)),
  };
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Call once at app start: drops RSVPs for events that ended over a day ago,
 * re-arms web reminders (and shows any missed one), and on Android opens the
 * event when a reminder notification is tapped. Returns a cleanup function.
 */
export function initEventReminders(navigate) {
  const now = Date.now();
  const current = readRsvps().filter((r) => r.item.event.end + DAY_MS > now);
  if (current.length !== readRsvps().length) writeRsvps(current);

  const markNotified = (id) =>
    writeRsvps(
      readRsvps().map((r) =>
        r.item.id === id
          ? { ...r, reminder: { ...r.reminder, notified: true } }
          : r
      )
    );
  const arm = () => armWebReminders(readRsvps(), markNotified);
  arm();
  window.addEventListener(EVENT, arm);

  let removeTapListener = () => {};
  listenForReminderTaps(navigate).then((remove) => {
    removeTapListener = remove;
  });
  return () => {
    window.removeEventListener(EVENT, arm);
    removeTapListener();
  };
}
